import React, { useContext } from "react";
import { Link } from "react-router-dom";
import CountUp from "react-countup";
import * as Icons from "lucide-react";
import {
  Phone, Calendar, ChevronRight, Quote, MapPin, Mail, Clock, PlayCircle, ArrowUpRight, Sparkles,
} from "lucide-react";
import { CLINIC, STATS, SERVICES, DOCTORS, TESTIMONIALS, WHY_CHOOSE, GALLERY } from "@/lib/data";
import { BookContext } from "@/components/Layout";
import { useLang } from "@/lib/i18n";

const iconFor = (name) => Icons[name] || Icons.CheckCircle;

const HEALTHY_TIPS = [
  { id: "1AQytW9t-eo", titleKey: "tips.t1", subKey: "tips.s1", thumb: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=900&q=80" },
  { id: "v7AYKMP6rOE", titleKey: "tips.t2", subKey: "tips.s2", thumb: "https://images.unsplash.com/photo-1545205597-3d9d02c29597?w=900&q=80" },
  { id: "QlrJiC3MzVQ", titleKey: "tips.t3", subKey: "tips.s3", thumb: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=900&q=80" },
  { id: "wZAjVQWbMlE", titleKey: "tips.t4", subKey: "tips.s4", thumb: "https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=900&q=80" },
];

export default function Home() {
  const { open } = useContext(BookContext);
  const { t } = useLang();

  return (
    <div data-testid="home-page">
      {/* ============== HERO ============== */}
      <section className="relative overflow-hidden bg-white" data-testid="hero">
        {/* Aurora + gradient background composition */}
        <div className="absolute inset-0">
          <div className="aurora-bg opacity-90" />
          <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-gold/12 blur-3xl float-slow" />
          <div className="absolute bottom-0 left-0 w-[440px] h-[440px] rounded-full bg-brand/14 blur-3xl" />
          <svg className="absolute top-1/4 right-10 w-24 h-24 text-gold/30 hidden lg:block float-slow" viewBox="0 0 100 100" fill="none">
            <circle cx="50" cy="50" r="48" stroke="currentColor" strokeWidth="1" strokeDasharray="2 4" />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center pt-12 pb-16 lg:pt-20 lg:pb-28">
          <div className="lg:col-span-7 space-y-6 relative">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand/5 border border-brand/15 backdrop-blur" data-testid="hero-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
              <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-brand">{t("hero.eyebrow")}</span>
            </div>

            <div>
              <h1 className="font-display font-semibold tracking-tight leading-[0.92]" data-testid="hero-title">
                <span className="block text-[2.6rem] sm:text-[3.6rem] lg:text-[5rem] xl:text-[5.6rem] text-transparent bg-clip-text bg-gradient-to-r from-[#fa9805] via-[#ffb24b] to-[#fa9805]">
                  {t("hero.title1")}
                </span>
                <span className="block text-2xl sm:text-3xl lg:text-4xl mt-5 text-deep italic font-normal">
                  {t("hero.title2")} <span className="text-gold not-italic font-medium mx-1">·</span> <span className="text-brand">{t("hero.title2b")}</span>
                </span>
              </h1>
              <div className="mt-5 flex items-center gap-3">
                <div className="w-12 h-px bg-gold" />
                <div className="text-[11px] tracking-[0.28em] uppercase text-gold font-bold">EST 1954 · NEW DELHI</div>
                <div className="w-12 h-px bg-gold" />
              </div>
            </div>

            <p className="text-ink-soft text-base sm:text-lg max-w-xl leading-relaxed" data-testid="hero-desc">
              {t("hero.desc")}
            </p>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button onClick={() => open()} className="group inline-flex items-center gap-2 px-7 py-4 bg-brand text-white rounded-full font-semibold shadow-lifted hover:bg-brand-600 transition-all relative overflow-hidden" data-testid="hero-book-btn">
                <span className="absolute inset-0 bg-gradient-to-r from-gold/0 via-gold/30 to-gold/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <Calendar className="w-4 h-4 relative" /> <span className="relative">{t("cta.book")}</span>
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform relative" />
              </button>
              <a href={`tel:${CLINIC.phone}`} className="inline-flex items-center gap-2 px-7 py-4 bg-white text-deep border border-deep/15 rounded-full font-semibold hover:border-deep/40 hover:bg-deep/[0.02] transition-all" data-testid="hero-call-btn">
                <Phone className="w-4 h-4 text-brand" /> {CLINIC.phone}
              </a>
            </div>
          </div>

          {/* Hero collage */}
          <div className="lg:col-span-5 relative" data-testid="hero-visual">
            <div className="relative">
              <div className="absolute -inset-3 bg-gradient-to-tr from-brand/30 via-gold/20 to-deep/15 rounded-[2.5rem] blur-2xl" />

              <div className="relative grid grid-cols-12 gap-3">
                <div className="col-span-12 sm:col-span-8 rounded-[1.75rem] overflow-hidden shadow-lifted border-4 border-white aspect-[4/5] tilt-card">
                  <img
                    src="https://images.unsplash.com/photo-1638202993928-7267aad84c31?w=1100&q=85"
                    alt="Doctor consultation"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="col-span-12 sm:col-span-4 grid sm:grid-rows-2 gap-3">
                  <div className="rounded-[1.5rem] overflow-hidden border-4 border-white shadow-soft aspect-square float-slow">
                    <img src="https://images.unsplash.com/photo-1666214280352-db292c05fd80?w=600&q=85" alt="MRI" className="w-full h-full object-cover" />
                  </div>
                  <div className="rounded-[1.5rem] bg-deep text-white p-5 flex flex-col justify-between aspect-square sm:aspect-auto relative overflow-hidden">
                    <div className="absolute inset-0 opacity-40 bg-gradient-to-br from-gold/30 via-transparent to-transparent" />
                    <div className="relative">
                      <div className="text-[9px] uppercase tracking-[0.25em] text-gold font-bold mb-2">{t("common.yearsCaring")}</div>
                      <div className="font-display text-3xl font-semibold leading-none">70+</div>
                    </div>
                    <div className="text-xs text-white/70 mt-3 relative">{t("common.eastDelhi")}</div>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-5 left-5 right-5 sm:right-auto sm:max-w-xs bg-white rounded-2xl shadow-lifted border border-black/5 p-4 flex items-center gap-3 z-10 glow-ring">
                <div className="w-11 h-11 rounded-xl bg-gold/15 text-gold flex items-center justify-center shrink-0">
                  <Sparkles className="w-5 h-5" strokeWidth={1.7} />
                </div>
                <div>
                  <div className="text-xs text-ink-soft">Advanced imaging</div>
                  <div className="font-semibold text-deep text-sm leading-tight">1.5T MRI · 16-Slice CT</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust ribbon */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 lg:pb-12">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 bg-deep rounded-2xl p-5 sm:p-6 text-white relative overflow-hidden glow-ring">
            <div className="absolute inset-0 aurora-bg opacity-30" />
            {[
              { v: "1.5T", l: t("trust.1") },
              { v: "16-Slice", l: t("trust.2") },
              { v: "₹0", l: t("trust.3") },
              { v: "<24h", l: t("trust.4") },
            ].map((x, i) => (
              <div key={i} className={`relative px-3 ${i < 3 ? "sm:border-r border-white/10" : ""}`} data-testid={`hero-ribbon-${i}`}>
                <div className="font-display text-2xl sm:text-3xl text-gold font-semibold">{x.v}</div>
                <div className="text-[11px] tracking-[0.15em] uppercase text-white/65 font-semibold mt-1">{x.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Marquee strip */}
        <div className="relative border-y border-black/5 bg-white py-5 overflow-hidden">
          <div className="flex whitespace-nowrap marquee-track">
            {Array.from({ length: 2 }).map((_, dup) => (
              <div key={dup} className="flex items-center gap-12 px-6 shrink-0">
                {[
                  "1.5 Tesla MRI", "16-Slice CT", "NABL Path Lab", "Free Dialysis",
                  "Digital X-Ray", "Colour Doppler", "Bone Densitometry", "Endoscopy",
                  "ECHO · TMT", "Dental Implants", "OPG Imaging", "Charitable Pricing",
                ].map((m, i) => (
                  <React.Fragment key={`${dup}-${i}`}>
                    <span className="font-display text-lg sm:text-xl text-deep font-medium tracking-tight">{m}</span>
                    <span className="text-gold">✦</span>
                  </React.Fragment>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== STATS ============== */}
      <section className="bg-white border-y border-black/5" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-px bg-black/5">
          {[
            { value: 70, suffix: "+", label: t("hero.stat.years") },
            { value: 500000, suffix: "+", label: t("hero.stat.patients") },
            { value: 30, suffix: "+", label: t("hero.stat.doctors") },
            { value: 20, suffix: "+", label: t("hero.stat.services") },
          ].map((s, i) => (
            <div key={i} className="bg-white py-12 px-6 text-center" data-testid={`stat-${i}`}>
              <div className="font-display text-4xl sm:text-5xl text-deep font-semibold">
                <CountUp end={s.value} duration={2.4} separator="," enableScrollSpy scrollSpyOnce />
                <span className="text-gold">{s.suffix}</span>
              </div>
              <div className="mt-2 text-xs tracking-[0.18em] uppercase text-ink-soft font-semibold">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============== SERVICES ============== */}
      <section className="py-20 sm:py-28" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="text-[11px] tracking-[0.25em] uppercase text-gold font-bold mb-3">{t("sec.services.eyebrow")}</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-deep tracking-tight leading-tight">
                {t("sec.services.title1")} <span className="italic text-brand">{t("sec.services.title2")}</span>
              </h2>
            </div>
            <Link to="/services" className="inline-flex items-center gap-2 text-sm font-semibold text-brand hover:gap-3 transition-all" data-testid="all-services-link">
              {t("sec.services.cta")} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.slice(0, 6).map((s) => {
              const Icon = iconFor(s.icon);
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="group relative overflow-hidden bg-white rounded-3xl border border-black/5 shadow-soft hover:shadow-lifted hover:-translate-y-1 transition-all duration-300"
                  data-testid={`service-card-${s.slug}`}
                >
                  <div className="aspect-[16/10] overflow-hidden relative">
                    <img src={s.image} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 bg-white/95 backdrop-blur rounded-full text-[10px] tracking-[0.2em] uppercase text-gold font-bold">{s.short}</div>
                  </div>
                  <div className="p-6 sm:p-7">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="w-11 h-11 rounded-2xl bg-brand/10 flex items-center justify-center text-brand">
                        <Icon className="w-5 h-5" strokeWidth={1.7} />
                      </div>
                      <ArrowUpRight className="w-5 h-5 text-deep/40 group-hover:text-brand group-hover:rotate-45 transition-all duration-300" />
                    </div>
                    <h3 className="font-display text-2xl text-deep font-semibold mb-2">{s.name}</h3>
                    <p className="text-sm text-ink-soft line-clamp-2">{s.blurb}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== WHY CHOOSE (renamed) ============== */}
      <section className="py-20 sm:py-28 bg-deep text-white relative overflow-hidden" data-testid="why-choose-section">
        <div className="absolute inset-0 subtle-grain opacity-40 pointer-events-none" />
        <div className="absolute -top-32 right-0 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-[11px] tracking-[0.25em] uppercase text-gold font-bold mb-4">{t("sec.why.eyebrow")}</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.85rem] font-semibold tracking-tight leading-[1.1]" data-testid="why-title">
              {t("sec.why.title")}
            </h2>
            <p className="mt-5 text-white/70 leading-relaxed max-w-2xl mx-auto">{t("sec.why.subtitle")}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_CHOOSE.map((w, i) => {
              const Icon = iconFor(w.icon);
              const titleKey = `why.${["nabl", "tech", "experts", "charity", "dialysis", "heritage"][i]}.t`;
              const descKey = `why.${["nabl", "tech", "experts", "charity", "dialysis", "heritage"][i]}.d`;
              return (
                <div key={w.title} className="p-7 rounded-3xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:-translate-y-1 transition-all backdrop-blur-sm" data-testid={`why-card-${i}`}>
                  <div className="w-12 h-12 rounded-2xl bg-gold/15 flex items-center justify-center text-gold mb-5">
                    <Icon className="w-6 h-6" strokeWidth={1.6} />
                  </div>
                  <h3 className="font-display text-xl font-semibold mb-2">{t(titleKey)}</h3>
                  <p className="text-sm text-white/65 leading-relaxed">{t(descKey)}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <button onClick={() => open()} className="inline-flex items-center gap-2 px-7 py-4 bg-gold text-deep rounded-full font-semibold hover:bg-gold-600 transition-colors" data-testid="why-choose-cta">
              <Calendar className="w-4 h-4" /> {t("cta.bookNow")}
            </button>
          </div>
        </div>
      </section>

      {/* ============== VIDEO SECTION ============== */}
      <section className="py-20 sm:py-28 bg-background" data-testid="video-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-[11px] tracking-[0.25em] uppercase text-gold font-bold mb-4">{t("sec.video.eyebrow")}</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-deep tracking-tight leading-tight max-w-3xl mx-auto" data-testid="video-tagline">
            "{t("sec.video.tagline")}"
          </h2>
          <p className="mt-5 text-ink-soft max-w-xl mx-auto">{t("sec.video.subtitle")}</p>

          <div className="mt-12 relative rounded-[2rem] overflow-hidden shadow-lifted border-8 border-white bg-deep aspect-video max-w-5xl mx-auto" data-testid="video-frame">
            <iframe
              src="https://player.vimeo.com/video/1058212999?h=36f5829c4b&badge=0&autopause=0&player_id=0&app_id=58479&title=0&byline=0&portrait=0"
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              title="Shri Anandpur Trust"
            />
          </div>

          <div className="mt-10 inline-flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-soft border border-black/5">
            <div className="w-8 h-8 rounded-full bg-gold/15 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-gold" strokeWidth={1.7} />
            </div>
            <span className="text-sm font-semibold text-deep">Founded by Shri Paramhans Advait Math · 1954</span>
          </div>
        </div>
      </section>

      {/* ============== DOCTORS ============== */}
      <section className="py-20 sm:py-28" data-testid="doctors-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="text-[11px] tracking-[0.25em] uppercase text-gold font-bold mb-3">{t("sec.doctors.eyebrow")}</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-deep tracking-tight">
                {t("sec.doctors.title1")} <span className="italic text-brand">{t("sec.doctors.title2")}</span>
              </h2>
            </div>
            <Link to="/doctors" className="inline-flex items-center gap-2 text-sm font-semibold text-brand hover:gap-3 transition-all" data-testid="all-doctors-link">
              {t("sec.doctors.cta")} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {DOCTORS.slice(0, 4).map((d) => (
              <div key={d.name} className="bg-white rounded-3xl overflow-hidden border border-black/5 shadow-soft hover:shadow-lifted hover:-translate-y-1 transition-all" data-testid={`doctor-card-${d.name.replace(/\s+/g, '-')}`}>
                <div className="aspect-[4/5] overflow-hidden bg-deep/5">
                  <img src={d.image} alt={d.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="p-5">
                  <div className="text-[10px] tracking-[0.18em] uppercase text-gold font-bold mb-1">{d.specialty}</div>
                  <div className="font-display text-deep text-lg font-semibold leading-tight">{d.name}</div>
                  <div className="text-xs text-ink-soft mt-1 italic">{d.qual}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== TESTIMONIALS ============== */}
      <section className="py-20 sm:py-28 bg-brand/5" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <div className="text-[11px] tracking-[0.25em] uppercase text-gold font-bold mb-3">{t("sec.testimonials.eyebrow")}</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-deep tracking-tight">
              {t("sec.testimonials.title1")} <span className="italic text-brand">{t("sec.testimonials.title2")}</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {TESTIMONIALS.map((tst, i) => (
              <div key={tst.name} className={`p-7 rounded-3xl border ${i === 0 ? "lg:row-span-2 lg:col-span-2 bg-deep text-white border-transparent" : "bg-white border-black/5 shadow-soft"}`} data-testid={`testimonial-${i}`}>
                <Quote className={`w-8 h-8 mb-4 ${i === 0 ? "text-gold" : "text-brand/30"}`} strokeWidth={1.5} />
                <p className={`leading-relaxed ${i === 0 ? "text-xl font-display" : "text-sm"}`}>"{tst.text}"</p>
                <div className={`mt-6 pt-5 border-t ${i === 0 ? "border-white/10" : "border-black/5"}`}>
                  <div className={`font-semibold ${i === 0 ? "text-white" : "text-deep"}`}>{tst.name}</div>
                  <div className={`text-xs ${i === 0 ? "text-white/60" : "text-ink-soft"}`}>{tst.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== HEALTHY TIPS ============== */}
      <section className="py-20 sm:py-28" data-testid="healthy-tips-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="text-[11px] tracking-[0.25em] uppercase text-gold font-bold mb-3">{t("sec.tips.eyebrow")}</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-deep tracking-tight leading-tight">
                {t("sec.tips.title1")} <span className="italic text-brand">{t("sec.tips.title2")}</span>
              </h2>
              <p className="mt-4 text-ink-soft max-w-xl">{t("sec.tips.subtitle")}</p>
            </div>
          </div>

          {/* Featured video + 3 cards */}
          <div className="grid lg:grid-cols-12 gap-5">
            <div className="lg:col-span-7" data-testid="featured-tip">
              <div className="rounded-3xl overflow-hidden bg-deep aspect-video border-8 border-white shadow-soft">
                <iframe
                  src={`https://www.youtube.com/embed/${HEALTHY_TIPS[0].id}?rel=0`}
                  title="Healthy Tip"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="mt-5 px-2">
                <div className="text-[10px] tracking-[0.2em] uppercase text-gold font-bold mb-2">{t(HEALTHY_TIPS[0].subKey)}</div>
                <h3 className="font-display text-2xl text-deep font-semibold">{t(HEALTHY_TIPS[0].titleKey)}</h3>
              </div>
            </div>

            <div className="lg:col-span-5 grid sm:grid-cols-1 gap-4">
              {HEALTHY_TIPS.slice(1).map((tp, i) => (
                <a
                  key={tp.id}
                  href={`https://www.youtube.com/watch?v=${tp.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex gap-4 p-3 bg-white rounded-2xl border border-black/5 hover:shadow-soft hover:-translate-y-0.5 transition-all"
                  data-testid={`tip-${i + 1}`}
                >
                  <div className="relative w-32 sm:w-40 aspect-video rounded-xl overflow-hidden shrink-0">
                    <img src={tp.thumb} alt={t(tp.titleKey)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-0 flex items-center justify-center bg-deep/30 group-hover:bg-deep/10 transition-colors">
                      <PlayCircle className="w-9 h-9 text-white drop-shadow-lg" strokeWidth={1.2} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-gold font-bold mb-1">{t(tp.subKey)}</div>
                    <h4 className="font-display text-base text-deep font-semibold leading-snug line-clamp-2">{t(tp.titleKey)}</h4>
                    <span className="text-xs text-brand font-semibold mt-2 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                      Watch <ArrowUpRight className="w-3 h-3" />
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============== EMERGENCY STRIP ============== */}
      <section className="bg-gold/10 border-y border-gold/20" data-testid="emergency-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 rounded-2xl bg-gold text-deep flex items-center justify-center">
              <Phone className="w-7 h-7" strokeWidth={1.7} />
            </div>
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-gold-600 font-bold">{t("sec.emergency.eyebrow")}</div>
              <div className="font-display text-2xl text-deep font-semibold">{t("sec.emergency.title")}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={`tel:${CLINIC.phone}`} className="inline-flex items-center gap-2 px-6 py-3.5 bg-deep text-white rounded-full font-semibold" data-testid="emergency-call-btn">
              <Phone className="w-4 h-4" /> {CLINIC.phone}
            </a>
            <a href={`tel:${CLINIC.altPhone}`} className="inline-flex items-center gap-2 px-6 py-3.5 bg-white text-deep border border-deep/15 rounded-full font-semibold">
              {CLINIC.altPhone}
            </a>
          </div>
        </div>
      </section>

      {/* ============== GALLERY PREVIEW ============== */}
      <section className="py-20 sm:py-28" data-testid="gallery-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div>
              <div className="text-[11px] tracking-[0.25em] uppercase text-gold font-bold mb-3">{t("sec.gallery.eyebrow")}</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-deep tracking-tight max-w-xl">
                {t("sec.gallery.title1")} <span className="italic text-brand">{t("sec.gallery.title2")}</span>
              </h2>
            </div>
            <Link to="/gallery" className="text-sm font-semibold text-brand inline-flex items-center gap-2 hover:gap-3 transition-all" data-testid="gallery-link">
              {t("sec.gallery.cta")} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {GALLERY.slice(0, 6).map((g, i) => (
              <div key={i} className={`overflow-hidden rounded-2xl ${i === 0 ? "lg:col-span-2 lg:row-span-2" : ""}`} data-testid={`gallery-${i}`}>
                <img src={g.src} alt={g.caption} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== ADDRESS BANNER (NEW) ============== */}
      <section className="py-16 sm:py-24 bg-white relative overflow-hidden" data-testid="address-banner">
        <div className="absolute -left-20 top-1/2 -translate-y-1/2 w-96 h-96 bg-brand/8 rounded-full blur-3xl" />
        <div className="absolute -right-20 top-1/2 -translate-y-1/2 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 mb-6">
            <span className="w-10 h-px bg-gold" />
            <span className="text-[11px] tracking-[0.3em] uppercase text-gold font-bold">Our Address</span>
            <span className="w-10 h-px bg-gold" />
          </div>
          <p
            className="font-display font-bold text-brand text-[1.45rem] sm:text-[2rem] lg:text-[2.6rem] leading-[1.2] tracking-tight"
            data-testid="full-address"
          >
            <strong className="text-brand">Shri Anandpur Trust, C9/5, Krishna Nagar, New Delhi, 110051 (India)</strong>
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <a href={`tel:${CLINIC.altPhone.replace(/\s|-/g, "")}`} className="inline-flex items-center gap-2 px-6 py-3 bg-brand text-white rounded-full font-semibold shadow-soft hover:bg-brand-600 transition-all">
              <Phone className="w-4 h-4" /> {CLINIC.altPhone}
            </a>
            <a href={`mailto:${CLINIC.email}`} className="inline-flex items-center gap-2 px-6 py-3 bg-white text-deep border border-deep/15 rounded-full font-semibold">
              <Mail className="w-4 h-4 text-brand" /> {CLINIC.email}
            </a>
            <a href="https://www.google.com/maps/search/?api=1&query=Shri+Anandpur+Trust+Krishna+Nagar+Delhi" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-deep rounded-full font-semibold hover:bg-gold-600 transition-colors">
              <MapPin className="w-4 h-4" /> {t("sec.contact.directions")}
            </a>
          </div>
        </div>
      </section>

      {/* ============== CONTACT MAP ============== */}
      <section className="py-20 sm:py-28 bg-background" data-testid="contact-map-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16">
          <div>
            <div className="text-[11px] tracking-[0.25em] uppercase text-gold font-bold mb-3">{t("sec.contact.eyebrow")}</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-deep tracking-tight">
              {t("sec.contact.title1")} <span className="italic text-brand">{t("sec.contact.title2")}</span>
            </h2>
            <div className="mt-8 space-y-5">
              <Info icon={<MapPin />} label={t("common.address")}>{CLINIC.address}</Info>
              <Info icon={<Phone />} label={t("common.phone")}>
                <a href={`tel:${CLINIC.phone}`} className="hover:text-brand">{CLINIC.phone}</a>
                <span className="mx-2 text-ink-soft/40">/</span>
                <a href={`tel:${CLINIC.altPhone}`} className="hover:text-brand">{CLINIC.altPhone}</a>
              </Info>
              <Info icon={<Mail />} label={t("common.email")}>
                <a href={`mailto:${CLINIC.email}`} className="hover:text-brand">{CLINIC.email}</a>
              </Info>
              <Info icon={<Clock />} label={t("common.timings")}>
                {t("common.timings.value")}
                <div className="text-xs text-ink-soft mt-0.5">{t("common.report.note")}</div>
              </Info>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => open()} className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand text-white rounded-full font-semibold shadow-soft" data-testid="map-section-book">
                <Calendar className="w-4 h-4" /> {t("cta.book")}
              </button>
              <a href="https://www.google.com/maps/search/?api=1&query=Shri+Anandpur+Trust+Krishna+Nagar+Delhi" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-deep border border-deep/15 rounded-full font-semibold">
                <MapPin className="w-4 h-4 text-brand" /> {t("sec.contact.directions")}
              </a>
            </div>
          </div>
          <div className="rounded-3xl overflow-hidden border border-black/5 shadow-soft min-h-[420px]">
            <iframe
              title="Shri Anandpur Trust location"
              src="https://www.google.com/maps?q=Shri+Anandpur+Trust+Charitable+Diagnostic+Centre+Krishna+Nagar+Delhi&output=embed"
              width="100%"
              height="100%"
              className="min-h-[420px] w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="google-map"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function Info({ icon, label, children }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 rounded-2xl bg-brand/10 text-brand flex items-center justify-center shrink-0">
        {React.cloneElement(icon, { className: "w-5 h-5", strokeWidth: 1.6 })}
      </div>
      <div className="text-sm">
        <div className="text-xs tracking-[0.15em] uppercase text-ink-soft font-semibold mb-1">{label}</div>
        <div className="text-deep">{children}</div>
      </div>
    </div>
  );
}
